// src/pages/social/Conversation.jsx
import React, { useEffect, useContext, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FaArrowLeft, FaEllipsisV } from 'react-icons/fa';
import { UserContext } from '../../context/userContext';
import { MessageContext } from '../../context/messageContext';
import MessageList from '../../components/social/MessageList';
import MessageForm from '../../components/social/MessageForm';
import Avatar from '../../components/ui/Avatar';
import Loader from '../../components/common/Loader';

const Conversation = () => {
  const { id } = useParams(); // Conversation ID
  const { currentUser } = useContext(UserContext);
  const { 
    conversations, 
    activeConversation, 
    loading, 
    loadConversation, 
    fetchConversations 
  } = useContext(MessageContext);
  const [participant, setParticipant] = useState(null);
  const [showOptions, setShowOptions] = useState(false);
  const navigate = useNavigate();

  // Redirect if not authenticated
  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
    }
  }, [currentUser, navigate]);

  // Load conversation data
  useEffect(() => {
    if (currentUser && id) {
      // If we don't have conversations yet, fetch them first
      if (conversations.length === 0) {
        fetchConversations().then(() => {
          loadConversation(id);
        });
      } else {
        loadConversation(id);
        
        // Find the other participant from the current conversation
        const conversation = conversations.find(c => c._id === id);
        if (conversation) {
          setParticipant(conversation.participant);
        }
      }
    }
  }, [currentUser, id, conversations, fetchConversations, loadConversation]);

  if (!currentUser) {
    return null; // Or loading state while redirecting
  }

  if (loading && !participant) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  return (
    <section className="py-6">
      <div className="container mx-auto px-4">
        <div className="bg-white rounded-xl shadow-sm overflow-hidden flex flex-col h-[calc(100vh-150px)]">
          {/* Conversation header */}
          <div className="flex items-center p-4 border-b border-gray-200">
            <Link 
              to="/messages" 
              className="text-gray-600 hover:text-primary mr-4 transition-colors"
            >
              <FaArrowLeft />
            </Link>
            
            {participant && (
              <div className="flex items-center">
                <div className="relative">
                  <Avatar 
                    user={participant} 
                    size="sm" 
                    className="w-10 h-10 rounded-full border-2 border-gray-200"
                  />
                  <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
                </div>
                <div className="ml-3">
                  <h2 className="font-medium text-gray-900">{participant.name}</h2>
                  <div className="text-xs text-green-500">Online</div>
                </div>
              </div>
            )}
            
            <div className="ml-auto relative">
              <button 
                className="p-2 text-gray-600 hover:text-gray-900 rounded-full hover:bg-gray-100 transition-colors"
                onClick={() => setShowOptions(!showOptions)}
              >
                <FaEllipsisV />
              </button>
              
              {showOptions && (
                <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-10">
                  <button 
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    onClick={() => {
                      // Handle block/report user action
                      setShowOptions(false);
                    }}
                  >
                    Block user
                  </button>
                  <button 
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    onClick={() => {
                      // Handle delete conversation action
                      setShowOptions(false);
                    }}
                  >
                    Delete conversation
                  </button>
                </div>
              )}
            </div>
          </div>
          
          {/* Messages container */}
          <div className="flex-1 overflow-y-auto bg-gray-50 p-4">
            <MessageList conversationId={id} />
          </div>
          
          {/* Message input */}
          <div className="border-t border-gray-200 p-4 bg-white">
            <MessageForm recipientId={participant?._id} />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Conversation;